import { useGetGraphqlDataQuery } from '@repo/services';
import {
  type BaseOptionsType,
  FILTERABLE_ENTITY_TYPES,
  NON_FILTERABLE_LEAD_ATTRIBUTES,
} from 'advanced-filters/data/options';
import { getLeadAttributes } from 'advanced-filters/queries/leadAttributes';
import { pendingFilterSelector } from 'advanced-filters/selectors/filter';
import { modifyPendingFilter, setFetchError } from 'advanced-filters/store/filter';
import { type ConditionKeyOptionsType, transformOptionsForSelect } from 'advanced-filters/utils/conditionKey';
import { Select } from 'antd';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

const transformLeadAttributesData = (data: any[], type: string) => data?.filter(
  (d) => !NON_FILTERABLE_LEAD_ATTRIBUTES.includes(d.schemaName),
).map((d) => ({
  ...d,
  entityType: type,
  optionTitle: 'Lead Attributes',
}));

/**
 * Renders Dropdown for Base Condition Key
 */
export default function ConditionKeySelect({
  conditionKeys,
}: {
  conditionKeys: BaseOptionsType[]
}) {
  const [options, setOptions] = useState<ConditionKeyOptionsType[]>([]);

  const dispatch = useDispatch();
  const {
    conditionKey,
  } = useSelector(pendingFilterSelector);

  const {
    data,
    isLoading: attributesLoading,
    error,
  } = useGetGraphqlDataQuery({ document: getLeadAttributes });

  useEffect(() => {
    if (error) {
      dispatch(setFetchError('Failed To Fetch Lead Properties'));
    }
  }, [dispatch, error]);

  useEffect(() => {
    // Re-sync whenever the available condition keys change (e.g. feature-gated keys
    // like the Deal filter arrive after the async feature-config fetch resolves) — not
    // just once, otherwise late-arriving keys are missed until a full page reload.
    if (!attributesLoading && data) {
      setOptions([
        ...conditionKeys,
        ...transformLeadAttributesData(data?.leadAttributes || [], FILTERABLE_ENTITY_TYPES.leadAttribute),
      ]);
    }
  }, [attributesLoading, data, conditionKeys]);

  const handleChange = useCallback((value: string) => {
    const option = options.find((o) => o.value === value);
    dispatch(modifyPendingFilter({ conditionKey: option, conditionComparator: undefined, conditionValue: undefined }));
  }, [dispatch, options]);

  useEffect(() => {
    dispatch(modifyPendingFilter({ conditionKey: options[0] }));
  }, [options, dispatch]);

  useEffect(() => {
    if (!conditionKey) {
      dispatch(modifyPendingFilter({ conditionKey: options[0] }));
    }
  }, [conditionKey, options, dispatch]);

  const parsedOptions = useMemo(() => transformOptionsForSelect(options), [options]);

  return (
    <Select
      showSearch
      placeholder="Select a Condition"
      loading={!options.length}
      disabled={!options.length}
      options={parsedOptions}
      optionFilterProp="label"
      value={conditionKey?.value}
      onChange={handleChange}
    />
  );
}
